import React from 'react';
import {Form, Formik} from 'formik';
import * as yup from 'yup';
import Box from '@material-ui/core/Box/Box';
import Button from '@material-ui/core/Button';
import {useDispatch, useSelector} from 'react-redux';
import {InputTextField} from '../../../@crema/components/InputTextField';
import {addBuyPrice} from 'redux/actions';
import {getExchangeRateBuyPrice} from 'redux/selectors';
import {
  usePositiveNumberValidation,
  getPositiveNumberYupSchema,
} from 'hooks/usePositiveNumberValidation';

interface IAddBuyPriceFields {
  buyPrice: number | string;
}

interface AddBuyPriceProps {
  disabledForm: boolean;
}

const MAX_SAFE_VALUE = Number.MAX_SAFE_INTEGER;
const MAX_DIGITS_MESSAGE = 'Price exceeds maximum limit (max 16 digits allowed)';

const validationSchema = yup.object({
  buyPrice: getPositiveNumberYupSchema(
    'Buy price required',
    0.0001,
    MAX_SAFE_VALUE,
    MAX_DIGITS_MESSAGE,
  ).min(0.0001, 'Price must be greater than 0'),
});

export function AddBuyPrice({
  disabledForm,
}: AddBuyPriceProps): React.ReactElement {
  const dispatch = useDispatch();
  const buyPrice = useSelector(getExchangeRateBuyPrice);

  const {
    isInputValid,
    validateInput,
    connectFormik,
    handleKeyDown,
    handlePaste,
  } = usePositiveNumberValidation();

  const onSubmit = (data: IAddBuyPriceFields): void => {
    const numericValue =
      typeof data.buyPrice === 'string'
        ? parseFloat(data.buyPrice)
        : data.buyPrice;

    if (!isNaN(numericValue as number)) {
      dispatch(addBuyPrice(numericValue as number));
    }
  };

  return (
    <Formik
      enableReinitialize={true}
      initialValues={{
        buyPrice: buyPrice && buyPrice > 0 ? buyPrice : '',
      }}
      validationSchema={validationSchema}
      onSubmit={onSubmit}>
      {({setFieldError, setFieldTouched, isValid, dirty}) => {
        connectFormik(setFieldError, setFieldTouched);
        return (
          <Box textAlign='left' mt={8} clone>
            <Form>
              <Box display='flex'>
                <InputTextField
                  style={{marginRight: '40px'}}
                  type='number'
                  placeholder='Enter buy price'
                  name='buyPrice'
                  onKeyDown={handleKeyDown('buyPrice')}
                  onPaste={handlePaste('buyPrice', MAX_SAFE_VALUE, MAX_DIGITS_MESSAGE)}
                  onChange={validateInput('buyPrice', MAX_SAFE_VALUE, MAX_DIGITS_MESSAGE)}
                  onWheel={(e: any) => e.target.blur()}
                  onFocus={(e: any) => e.target.select()}
                  onClick={(e: any) => {
                    if (e.target && typeof e.target.select === 'function') {
                      e.target.select();
                    }
                  }}
                  inputProps={{
                    'aria-label': 'naked',
                    step: 'any',
                    min: '0',
                  }}
                />
                <Button
                  variant='contained'
                  color='primary'
                  disabled={disabledForm || !isInputValid || !isValid || !dirty}
                  type='submit'>
                  Save
                </Button>
              </Box>
            </Form>
          </Box>
        );
      }}
    </Formik>
  );
}
